import React, { useEffect, useMemo, useState } from 'react'
import { supabase } from './supabaseClient'
import { useAuth } from './AuthContext'
import {
  TrendingUp, TrendingDown, Wallet, Package, AlertCircle, CheckCircle, Clock,
  ChevronRight, ChevronDown, Maximize2, Minimize2, Download, ChevronLeft,
  Calendar, Search, Filter, Award, ShoppingCart, Users, DollarSign, Percent,
  ArrowUp, ArrowDown, ShieldOff,
} from 'lucide-react'
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis,
  CartesianGrid, Tooltip as RTooltip, Legend, PieChart, Pie, Cell,
} from 'recharts'

import { C } from './theme'
import LucroReal from './LucroReal'
import { CopyButton } from './CopyButton'
import { PORTES_COBRADOS_FALLBACK, PORTES_CUSTO_FALLBACK } from './portes'
import { lerTudo } from './lerTudo'

const fmt2 = (n) => Number(n || 0).toFixed(2)

// Fraccao do total que cada prestacao representa. Deixou de ser sempre 0,5:
// com o sinal flexivel a 1.a pode valer 8% e a 2.a 92%. Regra unica da app —
// o que entrou de facto > o sinal escolhido > metade.
const fracPrestacao = (enc, qual) => {
  const total = Number(enc?.total_final ?? enc?.total_com_iva ?? 0)
  if (!(total > 0)) return 0.5
  // '100' = paga-se tudo de uma vez: a 1.a prestacao vale o total e a 2.a zero.
  const p1 = (enc?.pago_1a_valor != null ? Number(enc.pago_1a_valor)
            : String(enc?.tipo_pagamento) === '100' ? total
            : enc?.sinal_escolhido != null ? Number(enc.sinal_escolhido)
            : total / 2) / total
  return qual === '1a' ? p1 : Math.max(0, 1 - p1)
}
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('pt-PT') : '—'
const MESES = ['Janeiro','Fevereiro','Março','Abril','Maio','Junho','Julho','Agosto','Setembro','Outubro','Novembro','Dezembro']
const MESES_CURTO = ['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez']

const margemColor = (pct) => pct >= 20 ? C.success : (pct >= 10 ? C.warning : C.danger)

// Portes — SÃO DOIS VALORES DIFERENTES e não podem ser confundidos:
//   COBRADOS ao cliente: 1.00 €/un — já embutido no preço de tabela, não sofre desconto
//   CUSTO para nós:      0.75 €/un — o que a LOGNOW paga ao transportador
// A diferença (+0.25 €/un) é margem nossa. Usar o mesmo valor dos dois lados apaga-a.


// Escalões de margem para o resumo da distribuição
const ESCALOES = [
  { id: 'lt15',   label: '< 15%',    min: -Infinity, max: 15,       cor: C.danger },
  { id: '15a20',  label: '15 – 20%', min: 15,        max: 20,       cor: C.warning },
  { id: '20a25',  label: '20 – 25%', min: 20,        max: 25,       cor: C.primary },
  { id: 'gte25',  label: '≥ 25%',    min: 25,        max: Infinity, cor: C.success },
]

// Períodos pre-set
const PERIODOS = [
  { id: 'mes_actual',     label: 'Mês actual' },
  { id: 'mes_anterior',   label: 'Mês anterior' },
  { id: 'trimestre',      label: 'Trimestre' },
  { id: 'semestre',       label: 'Últimos 6 meses' },
  { id: 'ytd',            label: 'YTD (ano)' },
  { id: 'ano_anterior',   label: 'Ano anterior' },
  { id: 'tudo',           label: 'Tudo' },
  { id: 'custom',         label: 'Período custom' },
]

function calcRangePeriod(periodoId, customIni, customFim) {
  const hoje = new Date()
  const ano = hoje.getFullYear(), mes = hoje.getMonth()
  if (periodoId === 'mes_actual') return { ini: new Date(ano, mes, 1), fim: new Date(ano, mes + 1, 0, 23,59,59) }
  if (periodoId === 'mes_anterior') return { ini: new Date(ano, mes - 1, 1), fim: new Date(ano, mes, 0, 23,59,59) }
  if (periodoId === 'trimestre') return { ini: new Date(ano, mes - 2, 1), fim: new Date(ano, mes + 1, 0, 23,59,59) }
  if (periodoId === 'semestre')  return { ini: new Date(ano, mes - 5, 1), fim: new Date(ano, mes + 1, 0, 23,59,59) }
  if (periodoId === 'ytd')       return { ini: new Date(ano, 0, 1),       fim: new Date(ano, 11, 31, 23,59,59) }
  if (periodoId === 'ano_anterior') return { ini: new Date(ano - 1, 0, 1), fim: new Date(ano - 1, 11, 31, 23,59,59) }
  if (periodoId === 'tudo') return { ini: new Date(2000,0,1), fim: new Date(2100,11,31) }
  if (periodoId === 'custom') return { ini: customIni ? new Date(customIni) : new Date(2000,0,1), fim: customFim ? new Date(customFim + 'T23:59:59') : new Date(2100,11,31) }
  return { ini: new Date(ano, mes, 1), fim: new Date(ano, mes + 1, 0, 23,59,59) }
}

function labelPeriodo(periodo, ini, fim) {
  const p = PERIODOS.find(x => x.id === periodo)
  if (!p) return ''
  if (periodo === 'custom') return `${fmtDate(ini)} – ${fmtDate(fim)}`
  return p.label
}

export default function LucroAnalise({ onVerFactura }) {
  const { isAdmin, perfil } = useAuth()
  const [periodo, setPeriodo] = useState('mes_actual')
  const [customIni, setCustomIni] = useState('')
  const [customFim, setCustomFim] = useState('')
  const [incluirInternos, setIncluirInternos] = useState(true)
  const [filtroCliente, setFiltroCliente] = useState('')
  const [filtroProduto, setFiltroProduto] = useState('')
  const [margemMinima, setMargemMinima] = useState('')
  const [expandedIds, setExpandedIds] = useState(new Set())
  const [encomendas, setEncomendas] = useState([])
  const [itens, setItens] = useState([])
  const [produtos, setProdutos] = useState([])
  const [internosIds, setInternosIds] = useState(new Set())
  const [custosReais, setCustosReais] = useState(new Map()) // (enc_id|nome) -> { custoReal, fase }
  const [loading, setLoading] = useState(true)
  // ─── Aba Margens (catálogo: preço actual vs custo actual) ───
  const [aba, setAba] = useState('real')          // 'real' | 'encomendas' | 'margens'
  const [perfis, setPerfis] = useState([])
  const [erroProdutos, setErroProdutos] = useState('')
  const [alvoMargem, setAlvoMargem] = useState(20)
  const [cenarioEmail, setCenarioEmail] = useState('')  // '' = sem desconto (Standard)
  const [soAbaixo, setSoAbaixo] = useState(true)
  const [soVisiveis, setSoVisiveis] = useState(true)
  const [buscaMargens, setBuscaMargens] = useState('')
  const [portesCobrados, setPortesCobrados] = useState(PORTES_COBRADOS_FALLBACK)
  const [portesCusto, setPortesCusto] = useState(PORTES_CUSTO_FALLBACK)
  // Nome do artigo → portes desse fornecedor. Vazio = usa o valor global.
  const [portesCustoPorNome, setPortesCustoPorNome] = useState(new Map())
  const [portesCobradosPorNome, setPortesCobradosPorNome] = useState(new Map())
  const [filtroEscalao, setFiltroEscalao] = useState('')   // '' = usa o toggle "só abaixo do alvo"
  const [soDoCliente, setSoDoCliente] = useState(false)    // esconder tudo o que não é do cliente escolhido
  const [verOcultos, setVerOcultos] = useState(false)      // modo: só produtos escondidos e sem dono

  useEffect(() => { if (isAdmin) carregar() }, [isAdmin])

  const carregar = async () => {
    setLoading(true)
    // Paginado (ver src/lerTudo.js). Sem isto a Produtos e a produtos_internos
    // chegavam cortadas nas 1000 linhas e os artigos de fora ficavam sem custo
    // — o lucro deste ecrã vinha por baixo do real e não se percebia porquê.
    const [encRes, itensRes, prodRes, custosRes, perfisInternosRes, configRes, linksRes, fornsRes] = await Promise.all([
      lerTudo(() => supabase.from('encomendas')
        // tipo_pagamento e obrigatorio aqui: sem ele o fracPrestacao acima
        // nunca ve o '100' e reparte o lucro 50/50 numa encomenda paga de uma vez.
        .select('id, numero_encomenda, cliente_id, cliente_email, cliente_nome, total_sem_iva, total_com_iva, total_final, desconto_percentagem, desconto_valor, estado, created_at, pago_1a_prestacao, pago_1a_data, pago_2a_prestacao, pago_2a_data, pago_1a_valor, sinal_escolhido, tipo_pagamento'),
        'id'),
      lerTudo(() => supabase.from('encomenda_itens')
        .select('id, encomenda_id, produto_nome, produto_ean, quantidade, preco_unitario, total_sem_iva'),
        'id'),
      // fornecedor_id: sem ele nao ha como saber que portes pagamos em cada
      // artigo, e este ecra descontava 0,75 EUR/un a todos — tambem aos de
      // fornecedores que nao cobram transporte nenhum.
      lerTudo(() => supabase.from('Produtos')
        .select('id, Nome, EAN, preco, preco_manual, visivel_catalogo, categoria, Email_Cliente, fornecedor_id'),
        'id'),
      lerTudo(() => supabase.from('produtos_internos')
        .select('produto_nome, preco_custo, preco_fornecedor'),
        'produto_nome'),
      supabase.from('perfis').select('id, nome, email, role, desconto_percentagem, portes_por_unidade, cliente_interno'),
      supabase.from('config_sistema').select('chave, valor'),
      // Custos REAIS por (encomenda, produto) — vindos das facturas do fornecedor
      lerTudo(() => supabase.from('compras_fornecedor_encomendas')
        .select('id, encomenda_id, compra_id, produto_nome, quantidade_alocada, custo_unitario_previsao, custo_unitario_final, compras_fornecedor:compra_id(fase, valor_sem_iva)'),
        'id'),
      supabase.from('fornecedores').select('id, portes_custo_por_unidade, portes_cobrados_por_unidade'),
    ])
    setEncomendas(encRes.data || [])
    setItens(itensRes.data || [])
    // AS OITO LEITURAS, e nao duas. Cada uma que falhe em silencio mente para o
    // lado optimista e de maneira diferente: sem `custosRes` os artigos ficam
    // sem custo e a margem parece inteira; sem `fornsRes` os portes vao a zero e
    // o lucro sobe; sem `linksRes` perde-se o custo real das facturas e fica so
    // a estimativa. Nenhuma delas esvazia o ecra — todas o deixam com ar de bom.
    const falhas = [
      ['Produtos', prodRes], ['Contas internas', perfisInternosRes],
      ['Encomendas', encRes], ['Itens das encomendas', itensRes],
      ['Custos internos', custosRes], ['Configuração', configRes],
      ['Custos reais por factura', linksRes], ['Fornecedores', fornsRes],
    ].filter(([, r]) => r?.error).map(([nome, r]) => nome + ': ' + r.error.message)
    setErroProdutos(falhas.join(' · '))
    const mapaCustos = new Map((custosRes.data || []).map(i => [i.produto_nome, Number(i.preco_custo)]))
    const mapaForn = new Map((custosRes.data || []).filter(i => i.preco_fornecedor != null).map(i => [i.produto_nome, Number(i.preco_fornecedor)]))
    const prodComCusto = (prodRes.data || []).map(p => ({ ...p, preco_custo: mapaCustos.has(p.Nome) ? mapaCustos.get(p.Nome) : null, preco_fornecedor: mapaForn.has(p.Nome) ? mapaForn.get(p.Nome) : null }))
    setProdutos(prodComCusto)
    // Portes por ARTIGO, a partir do fornecedor dele. Os dois valores sao
    // distintos e nunca se podem trocar (ver src/portes.js): COBRADOS entram na
    // receita e ficam fora do desconto, CUSTO sai do lucro. Havia fornecedores
    // a zero nos dois e o ecra usava na mesma 1,00 e 0,75 para toda a gente.
    const pCusto = new Map(), pCobr = new Map()
    const fCusto = new Map(), fCobr = new Map()
    for (const f of (fornsRes.data || [])) {
      if (f.portes_custo_por_unidade != null) fCusto.set(String(f.id), Number(f.portes_custo_por_unidade))
      if (f.portes_cobrados_por_unidade != null) fCobr.set(String(f.id), Number(f.portes_cobrados_por_unidade))
    }
    for (const p of prodComCusto) {
      if (!p.Nome) continue
      const k = String(p.fornecedor_id)
      if (fCusto.has(k)) pCusto.set(p.Nome, fCusto.get(k))
      if (fCobr.has(k)) pCobr.set(p.Nome, fCobr.get(k))
    }
    setPortesCustoPorNome(pCusto)
    setPortesCobradosPorNome(pCobr)
    setPerfis(perfisInternosRes.data || [])
    setInternosIds(new Set((perfisInternosRes.data || []).filter(r => r.cliente_interno).map(r => r.id)))
    // Portes: config_sistema é a fonte de verdade (mesma chave que o Catálogo lê)
    const lerConfig = (chave, fallback) => {
      const raw = (configRes.data || []).find(r => r.chave === chave)?.valor
      const v = parseFloat(String(raw ?? '').replace(',', '.'))
      return (!isNaN(v) && v >= 0) ? v : fallback
    }
    setPortesCobrados(lerConfig('portes_por_unidade', PORTES_COBRADOS_FALLBACK))
    setPortesCusto(lerConfig('portes_custo_por_unidade', PORTES_CUSTO_FALLBACK))
    // -- O TOTAL DA FACTURA MANDA NO CUSTO DAS LINHAS --------------------
    //
    // As alocacoes sao calculadas com o desconto gravado na ficha do
    // fornecedor. Na factura 105 a Plus Skill deu doze por cento e a ficha diz
    // dez: cada linha ficou dois por cento acima do papel, e como este ecra
    // PREFERE a alocacao ao preco_custo — que estava certo — o lucro aparecia
    // mais baixo do que e. A ironia e essa: o numero certo estava ali ao lado,
    // como plano B.
    //
    // Reparte-se a diferenca por todas as linhas da mesma factura. As alocacoes
    // sabem a proporcao entre artigos; o total esta escrito na factura.
    // Normaliza-se sempre ao valor_sem_iva (mercadoria) e nunca ao final, que
    // ja traz os portes — esses sao somados a parte mais abaixo e contariam
    // duas vezes.
    //
    // Tecto de dez por cento pela mesma razao da Analise Factura: isto assume a
    // factura toda alocada, e uma diferenca maior quer dizer outra coisa
    // qualquer, provavelmente mercadoria sem encomenda associada. Ai nao se
    // adivinha e deixa-se como esta.
    const linksBrutos = linksRes.data || []
    const somaPorCompra = new Map()
    for (const l of linksBrutos) {
      const q = Number(l.quantidade_alocada || 0)
      const cu = Number(l.custo_unitario_final) || Number(l.custo_unitario_previsao) || 0
      if (!l.compra_id || !(q > 0) || !(cu > 0)) continue
      const acc = somaPorCompra.get(l.compra_id) || { soma: 0, factura: Number(l.compras_fornecedor?.valor_sem_iva || 0) }
      acc.soma += q * cu
      somaPorCompra.set(l.compra_id, acc)
    }
    const factorPorCompra = new Map()
    for (const [id, { soma, factura }] of somaPorCompra) {
      if (!(soma > 0) || !(factura > 0)) continue
      const fac = factura / soma
      if (Math.abs(fac - 1) > 0.0001 && Math.abs(fac - 1) <= 0.10) factorPorCompra.set(id, fac)
    }

    // Mapa de custos reais: prefere custo_final, senao usa custo_previsao
    const m = new Map()
    linksBrutos.forEach(l => {
      const key = `${l.encomenda_id}|${l.produto_nome}`
      const cf = Number(l.custo_unitario_final) || null
      const cp = Number(l.custo_unitario_previsao) || null
      const efectivo = cf || cp
      if (efectivo) {
        const fac = factorPorCompra.get(l.compra_id) || 1
        m.set(key, { custoReal: efectivo * fac, fase: cf ? 'final' : 'previsao' })
      }
    })
    setCustosReais(m)
    setLoading(false)
  }

  const custoPorNome = useMemo(() => {
    const m = new Map()
    produtos.forEach(p => { if (p.preco_custo != null) m.set(p.Nome, Number(p.preco_custo)) })
    return m
  }, [produtos])

  const descPorEnc = useMemo(() => {
    const m = new Map()
    encomendas.forEach(e => m.set(e.id, Number(e.desconto_percentagem || 0)))
    return m
  }, [encomendas])

  const { ini: periodIni, fim: periodFim } = calcRangePeriod(periodo, customIni, customFim)

  // Cálculo do período anterior equivalente (para comparações)
  const periodoAnterior = useMemo(() => {
    const dur = periodFim - periodIni
    return {
      ini: new Date(periodIni.getTime() - dur - 1),
      fim: new Date(periodIni.getTime() - 1),
    }
  }, [periodIni, periodFim])

  // Construir linhas (encomenda + items detalhados) com lucro realizado no período
  // Lucro real: o desconto do cliente NÃO incide sobre os portes que lhe são cobrados (1.00),
  // e o custo leva os portes que nós pagamos ao transportador (0.75). A diferença é margem.
  // Custo: prefere REAL da factura fornecedor (compras_fornecedor_encomendas) se existir
  const PORTES_COBRADOS = portesCobrados
  const PORTES_CUSTO = portesCusto
  // Valor por artigo, com o global como alternativa para os que ainda não têm
  // fornecedor atribuído. É por aqui que passam todas as contas de lucro.
  const portesCustoDe = React.useCallback(
    (nome) => portesCustoPorNome.has(nome) ? portesCustoPorNome.get(nome) : PORTES_CUSTO,
    [portesCustoPorNome, PORTES_CUSTO])
  const portesCobradosDe = React.useCallback(
    (nome) => portesCobradosPorNome.has(nome) ? portesCobradosPorNome.get(nome) : PORTES_COBRADOS,
    [portesCobradosPorNome, PORTES_COBRADOS])
  const linhas = useMemo(() => {
    const detalhePorEnc = new Map()
    itens.forEach(it => {
      const qty = Number(it.quantidade || 0)
      const desc = descPorEnc.get(it.encomenda_id) || 0
      // 1º procura custo REAL (factura fornecedor); fallback p/ catalogo
      const real = custosReais.get(`${it.encomenda_id}|${it.produto_nome}`)
      const custoUnit = real ? real.custoReal : custoPorNome.get(it.produto_nome)
      const custoFonte = real ? real.fase : (custoPorNome.has(it.produto_nome) ? 'catalogo' : null) // 'previsao' | 'final' | 'catalogo'
      const precoUnitVenda = Number(it.preco_unitario || 0)
      const portesCobradosItem = qty * portesCobradosDe(it.produto_nome)   // cobrados ao cliente — fora do desconto
      const portesCustoItem = qty * portesCustoDe(it.produto_nome)         // pagos por nós ao transportador
      const receitaBruta = Number(it.total_sem_iva || 0)
      const descontoAplicado = Math.max(0, receitaBruta - portesCobradosItem) * desc / 100
      const receitaItem = receitaBruta - descontoAplicado
      const precoUnitLiq = qty > 0 ? receitaItem / qty : 0
      const custoProduto = custoUnit != null ? qty * custoUnit : null
      const custoItem = custoProduto != null ? custoProduto + portesCustoItem : null
      const lucroItem = custoItem != null ? receitaItem - custoItem : null
      const margemItem = (lucroItem != null && receitaItem > 0) ? (lucroItem / receitaItem) * 100 : null
      const lucroPorUn = (lucroItem != null && qty > 0) ? lucroItem / qty : null
      const arr = detalhePorEnc.get(it.encomenda_id) || []
      arr.push({
        id: it.id, nome: it.produto_nome, ean: it.produto_ean || null, qty,
        precoUnitVenda, precoUnitLiq, custoUnit, custoFonte,
        receitaItem, custoItem, lucroItem, margemItem, lucroPorUn,
        semCusto: custoUnit == null,
      })
      detalhePorEnc.set(it.encomenda_id, arr)
    })

    const noPeriodo = (d) => {
      if (!d) return false
      const dt = new Date(d)
      return dt >= periodIni && dt <= periodFim
    }

    const out = []
    encomendas.forEach(e => {
      if (e.estado === 'cancelado') return
      const interno = internosIds.has(e.cliente_id)
      if (interno && !incluirInternos) return
      // Filtros
      if (filtroCliente && !(e.cliente_nome || e.cliente_email || '').toLowerCase().includes(filtroCliente.toLowerCase())) return
      const items = (detalhePorEnc.get(e.id) || [])
      if (filtroProduto) {
        const matches = items.some(it => (it.nome || '').toLowerCase().includes(filtroProduto.toLowerCase()))
        if (!matches) return
      }
      if (items.length === 0) return
      const receita = items.reduce((s, it) => s + it.receitaItem, 0)
      const receitaSemCusto = items.filter(it => it.semCusto).reduce((s, it) => s + it.receitaItem, 0)
      const custo = items.reduce((s, it) => s + (it.custoItem || 0), 0)
      const lucro = items.filter(it => !it.semCusto).reduce((s, it) => s + (it.lucroItem || 0), 0)
      const receitaComCusto = items.filter(it => !it.semCusto).reduce((s, it) => s + it.receitaItem, 0)
      const margemPct = receitaComCusto > 0 ? (lucro / receitaComCusto) * 100 : 0
      const qtTotal = items.reduce((s, it) => s + it.qty, 0)
      const pago1Periodo = e.pago_1a_prestacao && noPeriodo(e.pago_1a_data)
      const pago2Periodo = e.pago_2a_prestacao && noPeriodo(e.pago_2a_data)
      let frac = 0
      if (pago1Periodo) frac += 0.5
      if (pago2Periodo) frac += 0.5
      const realizado = interno ? 0 : lucro * frac
      // Filtro margem
      if (margemMinima !== '' && margemPct < Number(margemMinima)) return
      // Fonte de custo da encomenda: 'final' se todos final, 'previsao' se todos previsao, 'misto' se mistura, 'catalogo' se sem facturas
      const fontes = new Set(items.map(it => it.custoFonte).filter(Boolean))
      let fonteCusto = 'catalogo'
      if (fontes.size === 1) {
        const f = [...fontes][0]
        fonteCusto = f === 'final' ? 'final' : f === 'previsao' ? 'previsao' : 'catalogo'
      } else if (fontes.has('final') || fontes.has('previsao')) {
        fonteCusto = 'misto'
      }
      // Encomendas internas (Cátia, etc.): mostrar com lucro 0 (não é venda real)
      const lucroFinal = interno ? 0 : lucro
      const margemFinal = interno ? 0 : margemPct
      out.push({
        id: e.id, numero: e.numero_encomenda || e.id.slice(0,8).toUpperCase(),
        cliente: e.cliente_nome || e.cliente_email, data: e.created_at,
        interno, receita, receitaSemCusto, custo, lucro: lucroFinal, margemPct: margemFinal, qtTotal,
        pago1: e.pago_1a_prestacao, pago1Periodo, pago2: e.pago_2a_prestacao, pago2Periodo,
        realizado, items: items.sort((a,b) => (b.lucroItem||0) - (a.lucroItem||0)),
        fonteCusto,
      })
    })
    // Ordenar por número de encomenda ascendente (0001, 0002, 0003, ...)
    return out.sort((a, b) => {
      const na = parseInt(String(a.numero || '').split('/')[0], 10) || 0
      const nb = parseInt(String(b.numero || '').split('/')[0], 10) || 0
      return na - nb
    })
  }, [encomendas, itens, custoPorNome, descPorEnc, internosIds, custosReais, periodIni, periodFim, incluirInternos, filtroCliente, filtroProduto, margemMinima, portesCobradosDe, portesCustoDe])

  // KPIs do período
  const kpis = useMemo(() => {
    let lucro = 0, faturado = 0, custo = 0, receita = 0, encsPagas = 0, qtyVendida = 0
    const lucroPorProduto = new Map()
    // NOTA: havia aqui um primeiro `linhas.forEach` que somava e logo a seguir
    // zerava tudo para recomeçar — trabalho deitado fora, com um
    // `faturado = faturado` no meio. Encontrado pelo ESLint (no-self-assign) e
    // removido: os resultados eram exactamente os mesmos.
    linhas.forEach(l => {
      const interno = l.interno
      if (interno) return // não contribui para KPIs financeiros
      let frac = 0
      if (l.pago1Periodo) frac += 0.5
      if (l.pago2Periodo) frac += 0.5
      if (frac > 0) {
        encsPagas += 1
        faturado += l.receita * frac
        custo += l.custo * frac
        lucro += l.lucro * frac
        qtyVendida += l.qtTotal * frac
        l.items.forEach(it => {
          if (it.lucroItem != null) {
            const cur = lucroPorProduto.get(it.nome) || { lucro: 0, qty: 0 }
            cur.lucro += it.lucroItem * frac
            cur.qty += it.qty * frac
            lucroPorProduto.set(it.nome, cur)
          }
        })
      }
    })
    const margem = faturado > 0 ? (lucro / faturado) * 100 : 0
    const lucroMedioEnc = encsPagas > 0 ? lucro / encsPagas : 0
    let melhorProduto = null
    lucroPorProduto.forEach((v, nome) => {
      if (!melhorProduto || v.lucro > melhorProduto.lucro) melhorProduto = { nome, ...v }
    })
    return { lucro, faturado, custo, margem, encsPagas, qtyVendida, lucroMedioEnc, melhorProduto }
  }, [linhas])

  // KPIs do período anterior (para comparação)
  const kpisAnt = useMemo(() => {
    const noPeriodoAnt = (d) => {
      if (!d) return false
      const dt = new Date(d)
      return dt >= periodoAnterior.ini && dt <= periodoAnterior.fim
    }
    let lucro = 0, faturado = 0
    encomendas.forEach(e => {
      if (e.estado === 'cancelado') return
      if (internosIds.has(e.cliente_id)) return
      const desc = descPorEnc.get(e.id) || 0
      const itensEnc = itens.filter(it => it.encomenda_id === e.id)
      let receitaEnc = 0, lucroEnc = 0
      itensEnc.forEach(it => {
        const qty = Number(it.quantidade || 0)
        // Preferir custo REAL da factura fornecedor (custosReais) sobre o do catálogo,
        // igual ao que a tabela detalhada faz. Sem isto os KPIs/gráfico ficam inflacionados
        // quando a factura final subiu o custo face à previsão.
        const real = custosReais.get(`${e.id}|${it.produto_nome}`)
        const custoUn = real ? real.custoReal : custoPorNome.get(it.produto_nome)
        const receitaBruta = Number(it.total_sem_iva || 0)
        const portesCobradosItem = qty * portesCobradosDe(it.produto_nome)
        const portesCustoItem = qty * portesCustoDe(it.produto_nome)
        const descontoAplicado = Math.max(0, receitaBruta - portesCobradosItem) * desc / 100
        const rec = receitaBruta - descontoAplicado
        receitaEnc += rec
        if (custoUn != null) lucroEnc += rec - qty * custoUn - portesCustoItem
      })
      let frac = 0
      if (e.pago_1a_prestacao && noPeriodoAnt(e.pago_1a_data)) frac += fracPrestacao(e, '1a')
      if (e.pago_2a_prestacao && noPeriodoAnt(e.pago_2a_data)) frac += fracPrestacao(e, '2a')
      if (frac > 0) {
        lucro += lucroEnc * frac
        faturado += receitaEnc * frac
      }
    })
    return { lucro, faturado }
  }, [encomendas, itens, custoPorNome, custosReais, descPorEnc, internosIds, periodoAnterior, portesCobradosDe, portesCustoDe])

  const variacaoLucro = (kpisAnt.lucro > 0) ? ((kpis.lucro - kpisAnt.lucro) / kpisAnt.lucro) * 100 : null
  const variacaoFat = (kpisAnt.faturado > 0) ? ((kpis.faturado - kpisAnt.faturado) / kpisAnt.faturado) * 100 : null

  // Evolução 12 meses (lucro + margem mensal)
  const serie12m = useMemo(() => {
    const hoje = new Date()
    const meses = []
    for (let i = 11; i >= 0; i--) {
      const d = new Date(hoje.getFullYear(), hoje.getMonth() - i, 1)
      meses.push({ key: `${d.getFullYear()}-${d.getMonth()}`, label: `${MESES_CURTO[d.getMonth()]}/${String(d.getFullYear()).slice(2)}`, lucro: 0, faturado: 0, margem: 0 })
    }
    const idxOf = (date) => {
      const dt = new Date(date)
      return meses.findIndex(m => m.key === `${dt.getFullYear()}-${dt.getMonth()}`)
    }
    encomendas.forEach(e => {
      if (e.estado === 'cancelado') return
      if (internosIds.has(e.cliente_id)) return
      const desc = descPorEnc.get(e.id) || 0
      const itensEnc = itens.filter(it => it.encomenda_id === e.id)
      let receitaEnc = 0, lucroEnc = 0
      itensEnc.forEach(it => {
        const qty = Number(it.quantidade || 0)
        // Preferir custo REAL da factura fornecedor (custosReais) sobre o do catálogo,
        // igual ao que a tabela detalhada faz. Sem isto os KPIs/gráfico ficam inflacionados
        // quando a factura final subiu o custo face à previsão.
        const real = custosReais.get(`${e.id}|${it.produto_nome}`)
        const custoUn = real ? real.custoReal : custoPorNome.get(it.produto_nome)
        const receitaBruta = Number(it.total_sem_iva || 0)
        const portesCobradosItem = qty * portesCobradosDe(it.produto_nome)
        const portesCustoItem = qty * portesCustoDe(it.produto_nome)
        const descontoAplicado = Math.max(0, receitaBruta - portesCobradosItem) * desc / 100
        const rec = receitaBruta - descontoAplicado
        receitaEnc += rec
        if (custoUn != null) lucroEnc += rec - qty * custoUn - portesCustoItem
      })
      // Cada prestacao leva a fraccao que representa, nao metade.
      const f1 = fracPrestacao(e, '1a'), f2 = fracPrestacao(e, '2a')
      if (e.pago_1a_prestacao && e.pago_1a_data) {
        const i = idxOf(e.pago_1a_data); if (i >= 0) { meses[i].lucro += lucroEnc * f1; meses[i].faturado += receitaEnc * f1 }
      }
      if (e.pago_2a_prestacao && e.pago_2a_data) {
        const i = idxOf(e.pago_2a_data); if (i >= 0) { meses[i].lucro += lucroEnc * f2; meses[i].faturado += receitaEnc * f2 }
      }
    })
    meses.forEach(m => { m.margem = m.faturado > 0 ? (m.lucro / m.faturado) * 100 : 0; m.lucro = Math.round(m.lucro * 100)/100; m.faturado = Math.round(m.faturado*100)/100; m.margem = Math.round(m.margem*10)/10 })
    return meses
  }, [encomendas, itens, custoPorNome, custosReais, descPorEnc, internosIds, portesCobradosDe, portesCustoDe])

  // Top 10 produtos por lucro (período)
  const topProdutos = useMemo(() => {
    const m = new Map()
    linhas.forEach(l => {
      if (l.interno) return
      let frac = 0
      if (l.pago1Periodo) frac += 0.5
      if (l.pago2Periodo) frac += 0.5
      if (frac === 0) return
      l.items.forEach(it => {
        if (it.lucroItem == null) return
        const cur = m.get(it.nome) || { qty: 0, lucro: 0, receita: 0 }
        // qty: quantidade REAL vendida (não fraccionada). Lucro/receita: proporcional ao realizado no período.
        cur.qty += it.qty
        cur.lucro += it.lucroItem * frac
        cur.receita += it.receitaItem * frac
        m.set(it.nome, cur)
      })
    })
    return Array.from(m.entries()).map(([nome, v]) => ({
      nome, qty: v.qty, lucro: v.lucro,
      margem: v.receita > 0 ? (v.lucro / v.receita) * 100 : 0,
    })).sort((a, b) => b.lucro - a.lucro).slice(0, 10)
  }, [linhas])

  // Top 10 clientes por lucro (período)
  const topClientes = useMemo(() => {
    const m = new Map()
    linhas.forEach(l => {
      if (l.interno) return
      let frac = 0
      if (l.pago1Periodo) frac += 0.5
      if (l.pago2Periodo) frac += 0.5
      if (frac === 0) return
      const key = l.cliente
      const cur = m.get(key) || { encs: 0, lucro: 0, faturado: 0 }
      cur.encs += 1 * frac
      cur.lucro += l.lucro * frac
      cur.faturado += l.receita * frac
      m.set(key, cur)
    })
    return Array.from(m.entries()).map(([cliente, v]) => ({
      cliente, encs: Math.round(v.encs * 10) / 10, lucro: v.lucro, faturado: v.faturado,
      margem: v.faturado > 0 ? (v.lucro / v.faturado) * 100 : 0,
    })).sort((a, b) => b.lucro - a.lucro).slice(0, 10)
  }, [linhas])

  // Alertas — produtos com margem < 15% (vendas no período)
  const alertas = useMemo(() => {
    const margemBaixa = topProdutos.filter(p => p.margem < 15)
    // Inclui os associados a cliente: são privados (visivel_catalogo=false) mas vendem-se na mesma
    const semCusto = produtos.filter(p => (p.visivel_catalogo || p.Email_Cliente) && p.preco_custo == null).length
    return { margemBaixa, semCusto }
  }, [topProdutos, produtos])

  // Insights
  const insights = useMemo(() => {
    const out = []
    if (variacaoLucro != null) {
      const arrow = variacaoLucro >= 0 ? '▲' : '▼'
      const cor = variacaoLucro >= 0 ? C.success : C.danger
      out.push({ texto: `Lucro do período ${arrow} ${Math.abs(variacaoLucro).toFixed(1)}% vs período anterior`, cor })
    }
    if (kpis.melhorProduto) {
      out.push({ texto: `Produto com maior contribuição: ${kpis.melhorProduto.nome} (${fmt2(kpis.melhorProduto.lucro)} €)`, cor: C.primary })
    }
    if (alertas.semCusto > 0) {
      out.push({ texto: `${alertas.semCusto} produto(s) visível(eis) no catálogo sem preço_custo — lucro real é maior`, cor: C.warning })
    }
    if (alertas.margemBaixa.length > 0) {
      out.push({ texto: `${alertas.margemBaixa.length} produto(s) do top com margem abaixo de 15%`, cor: C.warning })
    }
    if (kpis.encsPagas === 0) {
      out.push({ texto: 'Sem prestações pagas no período seleccionado', cor: C.textMuted })
    }
    return out
  }, [variacaoLucro, kpis, alertas])

  // Composição (PieChart)
  const composicao = useMemo(() => ([
    { name: 'Custo (fornecedor)', value: Math.max(0, kpis.custo), fill: C.warning },
    { name: 'Lucro', value: Math.max(0, kpis.lucro), fill: C.success },
  ]), [kpis])

  const toggleExpand = (id) => {
    setExpandedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id); else next.add(id)
      return next
    })
  }
  const expandirTudo = () => setExpandedIds(new Set(linhas.map(l => l.id)))
  const colapsarTudo = () => setExpandedIds(new Set())
  const todosExpandidos = linhas.length > 0 && linhas.every(l => expandedIds.has(l.id))

  const exportarCSV = () => {
    const headers = ['Encomenda','Cliente','Data','Interno','Produto','EAN','Qty','Preco_unit_venda','Preco_unit_custo','Receita_sIVA','Custo_sIVA','Lucro','Margem_%','Lucro_un','Realizado_Periodo']
    const rows = []
    linhas.forEach(l => {
      let frac = 0
      if (l.pago1Periodo) frac += 0.5
      if (l.pago2Periodo) frac += 0.5
      const real = l.interno ? 0 : l.lucro * frac
      l.items.forEach(it => {
        rows.push([
          l.numero, `"${(l.cliente||'').replace(/"/g, '""')}"`, fmtDate(l.data),
          l.interno ? 'Sim' : 'Nao',
          `"${(it.nome||'').replace(/"/g, '""')}"`, it.ean || '', it.qty,
          fmt2(it.precoUnitLiq).replace('.', ','),
          it.custoUnit != null ? fmt2(it.custoUnit).replace('.', ',') : '',
          fmt2(it.receitaItem).replace('.', ','),
          it.custoItem != null ? fmt2(it.custoItem).replace('.', ',') : '',
          it.lucroItem != null ? fmt2(it.lucroItem).replace('.', ',') : '',
          it.margemItem != null ? fmt2(it.margemItem).replace('.', ',') : '',
          it.lucroPorUn != null ? fmt2(it.lucroPorUn).replace('.', ',') : '',
          fmt2(real * (it.lucroItem != null && l.lucro > 0 ? it.lucroItem / l.lucro : 0)).replace('.', ','),
        ])
      })
    })
    const csv = [headers.join(';'), ...rows.map(r => r.join(';'))].join('\n')
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `lucro_${periodo}_${new Date().toISOString().slice(0,10)}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  // ─── ABA MARGENS ────────────────────────────────────────────────────────
  // Margem teórica de cada produto ao preço de tabela ACTUAL — não depende de
  // ter havido vendas. Mesma definição do resto da página (lucro / receita):
  //   receita = (preço − portes) × (1 − desconto) + portes   ← desconto não incide nos portes
  //   custo   = preco_custo + portes                          ← 0.75/un pagos ao transportador
  const cenario = useMemo(() => perfis.find(p => p.email === cenarioEmail) || null, [perfis, cenarioEmail])
  const descontoCenario = Number(cenario?.desconto_percentagem || 0)

  const clientesCenario = useMemo(
    () => perfis.filter(p => p.email).sort((a, b) => String(a.nome || a.email).localeCompare(String(b.nome || b.email))),
    [perfis]
  )

  // Emails de admin — um produto associado a um admin também só é visto por ele
  const adminEmails = useMemo(() => {
    const set = new Set(
      perfis.filter(p => p.role === 'admin' && p.email).map(p => String(p.email).trim().toLowerCase())
    )
    if (perfil?.email) set.add(String(perfil.email).trim().toLowerCase())
    return set
  }, [perfis, perfil])

  const margens = useMemo(() => {
    const alvo = Number(alvoMargem) || 0
    const q = buscaMargens.trim().toLowerCase()
    const d = descontoCenario / 100
    const avaliados = []
    const semCusto = []
    produtos.forEach(p => {
      const associado = p.Email_Cliente || null
      // Duas maneiras de um produto só ser visto pelo admin:
      //   (a) sem dono E fora do catálogo — ninguém lhe chega
      //   (b) associado a um admin — privado, e o dono é o próprio admin
      const escondidoPublico = !p.visivel_catalogo && !associado
      const donoAdmin = !!(associado && adminEmails.has(associado.trim().toLowerCase()))
      const soAdminVe = escondidoPublico || donoAdmin
      // ATENÇÃO: associar um produto a um cliente põe visivel_catalogo=false (Dashboard.jsx:311)
      // — é privado, não é inactivo. Excluí-lo aqui apagava da análise exactamente os produtos
      // que dão lucro real. Por isso "só visíveis" nunca se aplica a produtos com cliente.
      if (verOcultos) { if (!soAdminVe) return }
      else if (soVisiveis && escondidoPublico) return
      if (q && !String(p.Nome || '').toLowerCase().includes(q) && !String(p.EAN || '').toLowerCase().includes(q)) return
      const doCenario = !!(cenarioEmail && associado && associado.trim().toLowerCase() === cenarioEmail.trim().toLowerCase())
      const pvp = Number(p.preco || 0)
      if (p.preco_custo == null || !(pvp > 0)) {
        semCusto.push({ id: p.id, nome: p.Nome, ean: p.EAN, pvp, visivel: !!p.visivel_catalogo, semPreco: !(pvp > 0), associado, doCenario, soAdminVe })
        return
      }
      // Portes do fornecedor DESTE artigo. Com os valores globais, um artigo
      // sem portes aparecia nesta tabela com 0,75 EUR de custo a mais e 1,00
      // EUR de receita blindada ao desconto — a margem saía errada nos dois
      // sentidos, e é esta tabela que decide se um preço se mexe.
      const portesCobrArt = portesCobradosDe(p.Nome)
      const portesCustArt = portesCustoDe(p.Nome)
      const receita = (pvp - portesCobrArt) * (1 - d) + portesCobrArt
      const custo = Number(p.preco_custo) + portesCustArt
      const lucro = receita - custo
      const margem = receita > 0 ? (lucro / receita) * 100 : 0
      // Preço de tabela que atingiria o alvo: receita_alvo = custo / (1 − alvo)
      // O que a fórmula automática daria hoje: fornecedor bruto × 1.20 + portes cobrados
      const precoFormula = p.preco_fornecedor != null && Number(p.preco_fornecedor) > 0
        ? Math.round((Number(p.preco_fornecedor) * 1.20 + portesCobrArt) * 100) / 100
        : null
      const receitaAlvo = alvo < 100 ? custo / (1 - alvo / 100) : null
      const pvpAlvo = (receitaAlvo != null && d < 1) ? (receitaAlvo - portesCobrArt) / (1 - d) + portesCobrArt : null
      avaliados.push({
        id: p.id, nome: p.Nome, ean: p.EAN, manual: !!p.preco_manual,
        associado, visivel: !!p.visivel_catalogo,
        soAdminVe,   // só o admin lhe chega — por não ter dono e estar arquivado, ou por ser dele
        doCenario,   // produto associado ao cliente do cenário — dá lucro real com ele
        pvp, custoProduto: Number(p.preco_custo), custo, receita, lucro, margem,
        pvpAlvo, subida: pvpAlvo != null ? pvpAlvo - pvp : null,
        abaixo: margem < alvo,
        // Preço congelado: comprar um produto põe preco_manual=true (protecção de preço
        // ao dono). Se o fornecedor entretanto subiu, o preço fica para trás e a margem
        // cai sem ninguém dar por isso — é isto que detectamos aqui.
        precoFormula, desactualizado: precoFormula != null && precoFormula - pvp > 0.01,
        atrasoPreco: precoFormula != null ? precoFormula - pvp : null,
      })
    })
    // Associados ao cliente do cenário sempre à frente; dentro de cada grupo, pior margem primeiro
    avaliados.sort((a, b) => (Number(b.doCenario) - Number(a.doCenario)) || (a.margem - b.margem))
    const abaixo = avaliados.filter(r => r.abaixo)
    const media = avaliados.length ? avaliados.reduce((s, r) => s + r.margem, 0) / avaliados.length : 0
    const negativos = avaliados.filter(r => r.lucro < 0)
    // Distribuição por escalão — responde a "quantos abaixo de 15%" sem mexer no alvo
    const escaloes = ESCALOES.map(e => ({
      ...e, produtos: avaliados.filter(r => r.margem >= e.min && r.margem < e.max),
    }))
    // Produtos associados ao cliente do cenário (o lucro real dele)
    const doCenario = avaliados.filter(r => r.doCenario)
    const doCenarioSemCusto = semCusto.filter(r => r.doCenario)   // dele, mas sem custo → não avaliáveis
    // Preço congelado pela compra e já ultrapassado pelo custo do fornecedor
    const desactualizados = avaliados.filter(r => r.manual && r.desactualizado)
      .sort((a, b) => b.atrasoPreco - a.atrasoPreco)
    const mediaCenario = doCenario.length ? doCenario.reduce((s, r) => s + r.margem, 0) / doCenario.length : null
    const subidaTotal = abaixo.reduce((s, r) => s + Math.max(0, r.subida || 0), 0)
    // Ordena o bloco "sem custo" com os do cliente à frente
    semCusto.sort((a, b) => Number(b.doCenario) - Number(a.doCenario))
    return { avaliados, abaixo, semCusto, media, negativos, escaloes, doCenario, doCenarioSemCusto, mediaCenario, subidaTotal, desactualizados }
  }, [produtos, soVisiveis, buscaMargens, alvoMargem, descontoCenario, portesCobradosDe, portesCustoDe, cenarioEmail, verOcultos])

  // Com um cliente escolhido, os produtos DELE aparecem sempre e à frente — mesmo os que já
  // estão acima do alvo — porque o que interessa é rever o preço do que ele compra.
  const { linhasMargens, nDele } = useMemo(() => {
    const esc = ESCALOES.find(e => e.id === filtroEscalao)
    // Modo "ocultos": mostra a lista completa, sem o corte do alvo — o objectivo é inventariá-los
    if (verOcultos) {
      return { linhasMargens: esc ? margens.avaliados.filter(r => r.margem >= esc.min && r.margem < esc.max) : margens.avaliados, nDele: 0 }
    }
    const base = esc
      ? margens.avaliados.filter(r => r.margem >= esc.min && r.margem < esc.max)
      : (soAbaixo ? margens.abaixo : margens.avaliados)
    if (!cenarioEmail || margens.doCenario.length === 0) return { linhasMargens: base, nDele: 0 }
    const dele = margens.doCenario
    if (soDoCliente) return { linhasMargens: dele, nDele: dele.length }
    const idsDele = new Set(dele.map(r => r.id))
    return { linhasMargens: [...dele, ...base.filter(r => !idsDele.has(r.id))], nDele: dele.length }
  }, [margens, soAbaixo, filtroEscalao, cenarioEmail, soDoCliente, verOcultos])

  // Contagem independente dos filtros — tudo o que só o admin vê, pelas duas vias
  const soAdminVeLista = useMemo(() => {
    const semDono = produtos.filter(p => !p.visivel_catalogo && !p.Email_Cliente)
    const deAdmin = produtos.filter(p => p.Email_Cliente && adminEmails.has(String(p.Email_Cliente).trim().toLowerCase()))
    return { semDono, deAdmin, total: semDono.length + deAdmin.length }
  }, [produtos, adminEmails])

  // Perfis cujos portes na ficha não batem certo com a config do sistema
  const perfisPortesDesalinhados = useMemo(
    () => perfis.filter(p => p.email && p.portes_por_unidade != null && Number(p.portes_por_unidade) !== Number(PORTES_COBRADOS)),
    [perfis, PORTES_COBRADOS]
  )

  const exportarCsvMargens = () => {
    const headers = ['Produto', 'EAN', 'Manual', 'PVP_sIVA', 'Custo_produto', 'Portes', 'Custo_total', 'Receita', 'Lucro_un', 'Margem_%', `PVP_para_${alvoMargem}%`, 'Subida_necessaria']
    const rows = linhasMargens.map(r => [
      `"${String(r.nome || '').replace(/"/g, '""')}"`, r.ean || '', r.manual ? 'Sim' : 'Nao',
      fmt2(r.pvp).replace('.', ','), fmt2(r.custoProduto).replace('.', ','), fmt2(PORTES_CUSTO).replace('.', ','),
      fmt2(r.custo).replace('.', ','), fmt2(r.receita).replace('.', ','), fmt2(r.lucro).replace('.', ','),
      fmt2(r.margem).replace('.', ','),
      r.pvpAlvo != null ? fmt2(r.pvpAlvo).replace('.', ',') : '',
      r.subida != null ? fmt2(r.subida).replace('.', ',') : '',
    ])
    const csv = [headers.join(';'), ...rows.map(r => r.join(';'))].join('\n')
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `margens_${cenario ? String(cenario.nome || cenario.email).replace(/\W+/g, '-') : 'standard'}_${new Date().toISOString().slice(0, 10)}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  // Guard rail — depois de todos os hooks, para a ordem não mudar entre renders
  if (!isAdmin) {
    return (
      <div style={{ padding: '60px 28px', textAlign: 'center', color: C.danger }}>
        <ShieldOff size={48} style={{ margin: '0 auto 12px' }} />
        <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '700' }}>Acesso restrito</h2>
        <p style={{ margin: '6px 0 0', fontSize: '13px', color: C.textMuted }}>Esta página só está disponível para administradores.</p>
      </div>
    )
  }

  if (loading) {
    return (
      <div style={{ padding: '40px 28px', textAlign: 'center', color: C.textMuted, fontSize: '13px' }}>
        A carregar análise de lucro…
      </div>
    )
  }

  return (
    <div style={{ padding: '20px 28px', flex: 1, overflowY: 'auto' }}>

      {/* HEADER */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '14px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h1 style={{ fontSize: '22px', fontWeight: '600', color: C.text, margin: '0 0 3px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <DollarSign size={20} color={C.purple} /> Análise de Lucro
          </h1>
          <p style={{ fontSize: '12px', color: C.textMuted, margin: 0 }}>
            {aba === 'real'
              ? 'Factura do fornecedor contra as encomendas que ela cobre · lucro e tesouraria em separado · valores s/IVA'
              : aba === 'margens'
                ? 'Margem de tabela · preço actual vs custo actual · não depende de ter havido vendas · margem sobre receita s/IVA'
                : 'Lucro realizado · proporcional às prestações pagas · custos s/IVA (já com desc. fornecedor) · margem sobre receita s/IVA · internos excluídos'}
          </p>
        </div>
        {/* A aba "real" traz o seu próprio botão de exportar — este exportaria a aba errada */}
        {aba !== 'real' && (
          <button onClick={aba === 'margens' ? exportarCsvMargens : exportarCSV}
            style={{ height: '34px', padding: '0 14px', border: `1px solid ${C.border}`, borderRadius: '4px', background: C.surface, color: C.text, cursor: 'pointer', fontSize: '12px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Download size={13} /> {aba === 'margens' ? 'Exportar margens' : 'Exportar CSV detalhado'}
          </button>
        )}
      </div>

      {/* ABAS */}
      <div style={{ display: 'flex', gap: '2px', borderBottom: `1px solid ${C.border}`, marginBottom: '14px' }}>
        {[
          { id: 'real', label: 'Lucro real por factura', icon: Wallet },
          { id: 'encomendas', label: 'Lucro realizado', icon: TrendingUp },
          { id: 'margens', label: 'Margens de catálogo', icon: Percent, alerta: margens.abaixo.length },
        ].map(t => (
          <button key={t.id} onClick={() => setAba(t.id)}
            style={{
              padding: '9px 16px', border: 'none', background: 'transparent', cursor: 'pointer',
              fontSize: '13px', fontWeight: aba === t.id ? '800' : '600',
              color: aba === t.id ? C.text : C.textMuted,
              borderBottom: `2px solid ${aba === t.id ? C.purple : 'transparent'}`,
              marginBottom: '-1px', display: 'flex', alignItems: 'center', gap: '6px',
            }}>
            <t.icon size={13} color={aba === t.id ? C.purple : C.textMuted} /> {t.label}
            {t.alerta > 0 && (
              <span style={{ padding: '1px 6px', borderRadius: '8px', fontSize: '10px', fontWeight: '800', background: C.danger, color: '#fff' }}>{t.alerta}</span>
            )}
          </button>
        ))}
      </div>

      {aba === 'real' && <LucroReal onVerFactura={onVerFactura} />}

      {/* ═══ ABA MARGENS ═══ */}
      {aba === 'margens' && (
        <>
          {erroProdutos && (
            <div style={{ background: C.dangerSoft, border: `1px solid ${C.danger}`, borderRadius: '6px', padding: '10px 14px', marginBottom: '14px', fontSize: '12px', color: C.danger, fontWeight: '600' }}>
              Erro a ler produtos: {erroProdutos}
            </div>
          )}

          {/* Filtros da aba */}
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: '6px', padding: '12px 14px', marginBottom: '14px', display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center' }}>
            <label style={{ fontSize: '11px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              <Users size={11} style={{ marginRight: '4px', verticalAlign: 'middle' }} />Cenário:
            </label>
            <select value={cenarioEmail} onChange={e => setCenarioEmail(e.target.value)}
              title="O desconto do cliente entra no cálculo — a margem muda conforme quem compra"
              style={{ height: '30px', padding: '0 8px', border: `1px solid ${C.border}`, borderRadius: '3px', fontSize: '12px', background: C.surface, cursor: 'pointer', minWidth: '210px' }}>
              <option value="">Preço de tabela (sem desconto)</option>
              {clientesCenario.map(p => (
                <option key={p.id} value={p.email}>
                  {p.nome || p.email}{Number(p.desconto_percentagem) > 0 ? ` — ${p.desconto_percentagem}% desc` : ' — sem desc'}{p.cliente_interno ? ' (interno)' : ''}
                </option>
              ))}
            </select>
            <div style={{ height: '24px', width: '1px', background: C.border, margin: '0 4px' }} />
            <label style={{ fontSize: '11px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Alvo:</label>
            <input type="number" value={alvoMargem} onChange={e => setAlvoMargem(e.target.value)} min="0" max="99"
              style={{ height: '30px', padding: '0 8px', border: `1px solid ${C.border}`, borderRadius: '3px', fontSize: '12px', width: '70px' }} />
            <span style={{ fontSize: '12px', color: C.textMuted, marginLeft: '-4px' }}>%</span>
            <input type="text" placeholder="Filtrar produto / EAN…" value={buscaMargens} onChange={e => setBuscaMargens(e.target.value)}
              style={{ height: '30px', padding: '0 8px', border: `1px solid ${C.border}`, borderRadius: '3px', fontSize: '12px', minWidth: '180px' }} />
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: C.textMuted, cursor: 'pointer' }}>
              <input type="checkbox" checked={soAbaixo} onChange={e => setSoAbaixo(e.target.checked)} style={{ cursor: 'pointer', accentColor: C.purple }} />
              Só abaixo do alvo
            </label>
            <label title="Produtos associados a um cliente são privados (visivel_catalogo=false por design) e contam sempre, mesmo com esta opção ligada."
              style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: C.textMuted, cursor: 'pointer' }}>
              <input type="checkbox" checked={soVisiveis} onChange={e => setSoVisiveis(e.target.checked)} style={{ cursor: 'pointer', accentColor: C.purple }} />
              Só visíveis no catálogo <span style={{ color: C.textFaint }}>(produtos de cliente contam sempre)</span>
            </label>
            {cenario && margens.doCenario.length > 0 && !verOcultos && (
              <label title="Esconde tudo o que não está associado a este cliente"
                style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', fontWeight: '700', color: C.success, cursor: 'pointer' }}>
                <input type="checkbox" checked={soDoCliente} onChange={e => setSoDoCliente(e.target.checked)} style={{ cursor: 'pointer', accentColor: C.success }} />
                Só os produtos dele ({margens.doCenario.length})
              </label>
            )}
            <button onClick={() => setVerOcultos(v => !v)}
              title="Tudo o que só tu vês: sem dono e fora do catálogo, ou associado a um admin"
              style={{
                height: '30px', padding: '0 12px', borderRadius: '3px', cursor: 'pointer',
                fontSize: '11px', fontWeight: '800',
                border: `1px solid ${verOcultos ? C.text : C.border}`,
                background: verOcultos ? C.text : C.surface,
                color: verOcultos ? '#fff' : C.text,
              }}>
              {verOcultos ? '✕ Sair dos ocultos' : `Só o admin vê (${soAdminVeLista.total})`}
            </button>
            <span title="Vem de config_sistema.portes_por_unidade — o mesmo valor que o Catálogo usa. Altera lá e esta página acompanha."
              style={{
                marginLeft: 'auto', padding: '4px 9px', borderRadius: '3px', fontSize: '11px', fontWeight: '700',
                background: PORTES_COBRADOS > PORTES_CUSTO ? C.successSoft : C.warningSoft,
                color: PORTES_COBRADOS > PORTES_CUSTO ? C.success : C.warning,
                border: `1px solid ${PORTES_COBRADOS > PORTES_CUSTO ? C.success : C.warning}`,
              }}>
              Portes: cobra {fmt2(PORTES_COBRADOS)} € · paga {fmt2(PORTES_CUSTO)} € = {PORTES_COBRADOS >= PORTES_CUSTO ? '+' : ''}{fmt2(PORTES_COBRADOS - PORTES_CUSTO)} €/un
              {PORTES_COBRADOS <= PORTES_CUSTO && ' ⚠ sem margem nos portes'}
            </span>
          </div>

          {/* KPIs da aba */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '10px', marginBottom: '16px' }}>
            {[
              { titulo: 'Abaixo do alvo', valor: `${margens.abaixo.length}`, sub: `de ${margens.avaliados.length} avaliados`, cor: margens.abaixo.length ? C.danger : C.success, icon: AlertCircle, iconColor: margens.abaixo.length ? C.danger : C.success },
              { titulo: 'Margem média', valor: `${fmt2(margens.media)} %`, sub: cenario ? `com ${descontoCenario}% desc · ${cenario.nome || cenario.email}` : 'preço de tabela', cor: margens.media >= Number(alvoMargem) ? C.success : C.danger, icon: Percent, iconColor: C.purple },
              { titulo: 'Margem negativa', valor: `${margens.negativos.length}`, sub: margens.negativos.length ? 'produtos a dar prejuízo' : 'nenhum a dar prejuízo', cor: margens.negativos.length ? C.danger : C.success, icon: TrendingDown, iconColor: margens.negativos.length ? C.danger : C.success },
              { titulo: 'Sem custo registado', valor: `${margens.semCusto.length}`, sub: 'não avaliáveis — ponto cego', cor: margens.semCusto.length ? C.warning : C.textMuted, icon: AlertCircle, iconColor: C.warning },
              { titulo: 'Pior margem', valor: margens.avaliados.length ? `${fmt2(margens.avaliados[0].margem)} %` : '—', sub: margens.avaliados.length ? margens.avaliados[0].nome : 'sem dados', cor: margens.avaliados.length ? margemColor(margens.avaliados[0].margem) : C.textMuted, icon: TrendingDown, iconColor: C.danger },
            ].map(k => (
              <div key={k.titulo} style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: '6px', padding: '12px 14px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '10px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.3px', marginBottom: '6px' }}>
                  <k.icon size={12} color={k.iconColor} /> {k.titulo}
                </div>
                <div style={{ fontSize: '20px', fontWeight: '800', color: k.cor, lineHeight: '1' }}>{k.valor}</div>
                <div style={{ fontSize: '10px', color: C.textMuted, marginTop: '4px', minHeight: '12px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={k.sub}>{k.sub}</div>
              </div>
            ))}
          </div>

          {/* Modo ocultos — explica o critério exacto */}
          {verOcultos && (
            <div style={{ background: C.surfaceAlt, border: `1px solid ${C.borderStrong}`, borderLeft: `4px solid ${C.text}`, borderRadius: '6px', padding: '10px 14px', marginBottom: '16px', fontSize: '12px', color: C.text }}>
              <strong>Produtos que só o admin vê — {soAdminVeLista.total} no total.</strong>{' '}
              <span style={{ color: C.textMuted }} title={`${soAdminVeLista.semDono.length} sem dono e fora do catálogo · ${soAdminVeLista.deAdmin.length} associados a um admin`}>
                Nenhum cliente lhes chega, seja por não terem dono e estarem fora do catálogo, seja por estarem associados a ti — o resultado é o mesmo.
                Os filtros de alvo e de «só visíveis» estão desligados aqui: aparecem todos.
                {margens.semCusto.length > 0 && ` ${margens.semCusto.length} deles não têm custo ou preço registado e estão no bloco de baixo, fora da tabela.`}
              </span>
            </div>
          )}

          {/* Preços congelados que o fornecedor já ultrapassou */}
          {margens.desactualizados.length > 0 && (
            <div style={{ background: C.warningSoft, border: `1px solid ${C.warning}`, borderLeft: `4px solid ${C.warning}`, borderRadius: '6px', padding: '12px 14px', marginBottom: '16px' }}>
              <p style={{ margin: '0 0 6px', fontSize: '13px', fontWeight: '800', color: '#92400e', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <AlertCircle size={15} /> {margens.desactualizados.length} produto(s) com preço congelado abaixo do que a fórmula daria hoje
              </p>
              <p style={{ margin: '0 0 8px', fontSize: '11.5px', color: '#7c2d12', lineHeight: '1.6' }}>
                Comprar um produto marca-o como <strong>preço manual</strong> — é protecção de preço para o cliente que o comprou.
                Mas o custo do fornecedor continuou a subir e estes ficaram para trás. A margem está a ser comida em silêncio.
              </p>
              <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                {margens.desactualizados.slice(0, 20).map(r => (
                  <div key={r.id} style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', fontSize: '11.5px', padding: '4px 0', borderTop: `1px solid ${C.warning}40`, color: '#7c2d12' }}>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.nome}>{r.nome}</span>
                    <span style={{ whiteSpace: 'nowrap', fontWeight: '700' }}>
                      {fmt2(r.pvp)} € → fórmula daria <strong>{fmt2(r.precoFormula)} €</strong>
                      <span style={{ color: C.danger }}> (−{fmt2(r.atrasoPreco)} €)</span>
                    </span>
                  </div>
                ))}
                {margens.desactualizados.length > 20 && (
                  <p style={{ margin: '6px 0 0', fontSize: '10.5px', color: '#92400e' }}>… e mais {margens.desactualizados.length - 20}. Usa o «Exportar margens» para a lista completa.</p>
                )}
              </div>
            </div>
          )}

          {/* Distribuição por escalão de margem */}
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: '6px', padding: '12px 14px', marginBottom: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', marginBottom: '10px', flexWrap: 'wrap' }}>
              <p style={{ margin: 0, fontSize: '10px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                Distribuição por escalão {cenario ? `· ${cenario.nome || cenario.email}` : '· preço de tabela'}
              </p>
              {filtroEscalao && (
                <button onClick={() => setFiltroEscalao('')}
                  style={{ border: 'none', background: 'transparent', color: C.purple, cursor: 'pointer', fontSize: '11px', fontWeight: '700' }}>
                  limpar escalão
                </button>
              )}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '8px' }}>
              {margens.escaloes.map(e => {
                const activo = filtroEscalao === e.id
                const pct = margens.avaliados.length ? (e.produtos.length / margens.avaliados.length) * 100 : 0
                return (
                  <button key={e.id} onClick={() => setFiltroEscalao(activo ? '' : e.id)}
                    title={`Ver os ${e.produtos.length} produtos deste escalão`}
                    style={{
                      textAlign: 'left', cursor: 'pointer',
                      background: activo ? C.surfaceAlt : C.surface,
                      border: `1px solid ${activo ? e.cor : C.border}`,
                      borderLeft: `3px solid ${e.cor}`, borderRadius: '4px', padding: '8px 10px',
                    }}>
                    <div style={{ fontSize: '10px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase' }}>{e.label}</div>
                    <div style={{ fontSize: '18px', fontWeight: '800', color: e.cor, lineHeight: '1.2' }}>{e.produtos.length}</div>
                    <div style={{ fontSize: '10px', color: C.textMuted }}>{pct.toFixed(0)}% do catálogo</div>
                  </button>
                )
              })}
            </div>
          </div>

          {/* Produtos associados ao cliente do cenário */}
          {cenario && margens.doCenario.length > 0 && (
            <div style={{ background: C.successSoft, border: `1px solid ${C.success}`, borderRadius: '6px', padding: '10px 14px', marginBottom: '16px', fontSize: '12px', color: C.text, display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
              <Award size={15} color={C.success} />
              <strong>{margens.doCenario.length} produto(s) associados a {cenario.nome || cenario.email}</strong>
              <span style={{ color: C.textMuted }}>
                — margem média <strong style={{ color: margemColor(margens.mediaCenario) }}>{fmt2(margens.mediaCenario)}%</strong>
                {(() => {
                  const abaixoDele = margens.doCenario.filter(r => r.margem < Number(alvoMargem)).length
                  return abaixoDele > 0
                    ? <> · <strong style={{ color: C.danger }}>{abaixoDele} abaixo de {alvoMargem}%</strong></>
                    : <> · <strong style={{ color: C.success }}>todos acima de {alvoMargem}%</strong></>
                })()}
                {' '}· subir todos ao alvo custaria <strong>+{fmt2(margens.doCenario.reduce((s, r) => s + Math.max(0, r.subida || 0), 0))} €</strong> no total dos preços.
                {' '}Aparecem <strong>todos no topo</strong>, mesmo os que já estão acima do alvo.
                {margens.doCenarioSemCusto.length > 0 && (
                  <strong style={{ color: C.warning }}>
                    {' '}Atenção: mais {margens.doCenarioSemCusto.length} produto(s) dele sem custo ou sem preço registado — não dá para calcular margem. Estão listados em baixo.
                  </strong>
                )}
              </span>
            </div>
          )}

          {/* Portes desalinhados nas fichas de cliente */}
          {perfisPortesDesalinhados.length > 0 && (
            <div style={{ background: C.warningSoft, border: `1px solid ${C.warning}`, borderRadius: '6px', padding: '10px 14px', marginBottom: '16px', fontSize: '12px', color: '#92400e' }}>
              <strong><AlertCircle size={13} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
              {perfisPortesDesalinhados.length} ficha(s) de cliente ainda com <code>portes_por_unidade</code> diferente de {fmt2(PORTES_COBRADOS)} €:</strong>{' '}
              {perfisPortesDesalinhados.map(p => `${p.nome || p.email} (${fmt2(p.portes_por_unidade)} €)`).join(', ')}.
              {' '}<strong style={{ color: C.danger }}>Isto AFECTA o total das encomendas deles.</strong> Dizia-se aqui que já não afectava —
              não é verdade: a RPC protege do desconto <code>quantidade × portes da ficha</code>, e uma ficha com
              0,75 € em vez de 1,00 € muda o desconto e portanto o total a pagar. Na encomenda 0015/2026 deu
              77 cêntimos de diferença entre o que o cliente pagou e o que as linhas para factura mostravam.
              {' '}Corrigir em <strong>Utilizadores</strong> — mas atenção: só afecta encomendas NOVAS. As antigas
              ficam com o total que já foi calculado e pago, e é por esse que se factura.
            </div>
          )}

          {/* Veredicto */}
          <div style={{
            background: margens.abaixo.length ? C.dangerSoft : C.successSoft,
            border: `1px solid ${margens.abaixo.length ? C.danger : C.success}`,
            borderRadius: '6px', padding: '12px 14px', marginBottom: '16px',
            fontSize: '13px', color: margens.abaixo.length ? C.danger : C.success, fontWeight: '700',
            display: 'flex', alignItems: 'center', gap: '8px',
          }}>
            {margens.abaixo.length
              ? <><AlertCircle size={16} /> {margens.abaixo.length} de {margens.avaliados.length} produtos ficam abaixo de {alvoMargem}% {cenario ? `para ${cenario.nome || cenario.email}` : 'a preço de tabela'}.</>
              : <><CheckCircle size={16} /> Todos os {margens.avaliados.length} produtos avaliados atingem {alvoMargem}% {cenario ? `para ${cenario.nome || cenario.email}` : 'a preço de tabela'}.</>}
          </div>

          {/* Tabela */}
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: '6px', overflow: 'hidden', marginBottom: '16px' }}>
            <div style={{ padding: '10px 14px', borderBottom: `1px solid ${C.border}`, fontSize: '11px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.3px' }}>
              {verOcultos
                ? <>Produtos ocultos sem dono ({linhasMargens.length} com margem calculável)</>
                : nDele > 0
                  ? <>Produtos de <span style={{ color: C.success }}>{cenario?.nome || cenario?.email}</span> ({nDele}) primeiro{!soDoCliente && `, depois ${linhasMargens.length - nDele} do resto do catálogo`}</>
                  : <>{soAbaixo ? 'Produtos abaixo do alvo' : 'Todos os produtos avaliados'} ({linhasMargens.length})</>}
              {' '}· ordenados da pior margem para a melhor
            </div>
            {linhasMargens.length === 0 ? (
              <p style={{ margin: 0, padding: '30px', textAlign: 'center', fontSize: '13px', color: C.textMuted }}>
                {margens.avaliados.length === 0 ? 'Nenhum produto com preço e custo registados.' : 'Nenhum produto abaixo do alvo — bom sinal.'}
              </p>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                  <thead>
                    <tr style={{ background: C.bg, borderBottom: `1px solid ${C.border}` }}>
                      {['Produto', 'PVP s/IVA', 'Custo', 'Portes', 'Lucro/un', 'Margem', `PVP p/ ${alvoMargem}%`, 'Subir'].map((h, i) => (
                        <th key={h} style={{ padding: '8px 10px', textAlign: i === 0 ? 'left' : 'right', fontSize: '10px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {linhasMargens.map((r, i) => (
                      <React.Fragment key={r.id}>
                      {/* Separador entre os produtos do cliente e o resto do catálogo */}
                      {nDele > 0 && i === nDele && (
                        <tr>
                          <td colSpan={8} style={{ padding: '8px 10px', background: C.bg, borderTop: `2px solid ${C.border}`, borderBottom: `1px solid ${C.border}`, fontSize: '10px', fontWeight: '800', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                            Restantes produtos do catálogo — não associados a {cenario?.nome || cenario?.email}
                          </td>
                        </tr>
                      )}
                      <tr style={{
                        borderBottom: `1px solid ${C.border}`,
                        background: r.doCenario ? C.successSoft : 'transparent',
                        boxShadow: r.doCenario ? `inset 3px 0 0 ${C.success}` : 'none',
                      }}>
                        <td style={{ padding: '8px 10px', maxWidth: '340px' }}>
                          <div style={{ fontWeight: '700', color: C.text, display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                            {r.doCenario && (
                              <span title={`Produto associado a ${r.associado} — é aqui que está o lucro real deste cliente`}
                                style={{ padding: '1px 6px', borderRadius: '3px', fontSize: '9px', fontWeight: '800', background: C.success, color: '#fff' }}>DELE</span>
                            )}
                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '260px' }} title={r.nome}>{r.nome}</span>
                            {r.manual && <span title="Preço inserido à mão — não segue a fórmula automática (fornecedor × 1.20 + portes)" style={{ padding: '1px 5px', borderRadius: '3px', fontSize: '9px', fontWeight: '800', background: C.warningSoft, color: C.warning }}>MANUAL</span>}
                            {/* Etiqueta única: as duas vias (sem dono + arquivado, ou dono admin)
                                dão o mesmo resultado prático — só o admin vê o produto. */}
                            {r.soAdminVe && (
                              <span title={r.associado ? `Associado a ${r.associado} (admin) — privado, só tu o vês` : 'Sem cliente associado e fora do catálogo — só tu o vês'}
                                style={{ padding: '1px 5px', borderRadius: '3px', fontSize: '9px', fontWeight: '800', background: C.text, color: '#fff' }}>ADMIN</span>
                            )}
                            {r.associado && !r.doCenario && <span title={`Associado a ${r.associado}`} style={{ fontSize: '9px', color: C.success, fontWeight: '700' }}>◆ {r.associado.split('@')[0]}</span>}
                          </div>
                          {r.ean && <div style={{ fontSize: '10px', color: C.textMuted, fontFamily: 'monospace' }}>{r.ean}</div>}
                        </td>
                        <td style={{ padding: '8px 10px', textAlign: 'right', color: C.text, whiteSpace: 'nowrap' }}>{fmt2(r.pvp)} €</td>
                        <td style={{ padding: '8px 10px', textAlign: 'right', color: C.textMuted, whiteSpace: 'nowrap' }}>{fmt2(r.custoProduto)} €</td>
                        <td style={{ padding: '8px 10px', textAlign: 'right', color: C.textMuted, whiteSpace: 'nowrap' }} title={`Custo real dos portes. Ao cliente são cobrados ${fmt2(PORTES_COBRADOS)} €/un.`}>{fmt2(PORTES_CUSTO)} €</td>
                        <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: '700', color: r.lucro >= 0 ? C.success : C.danger, whiteSpace: 'nowrap' }}>{fmt2(r.lucro)} €</td>
                        <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: '800', color: r.margem >= Number(alvoMargem) ? C.success : (r.margem >= Number(alvoMargem) / 2 ? C.warning : C.danger), whiteSpace: 'nowrap' }}>
                          {fmt2(r.margem)} %
                        </td>
                        <td style={{ padding: '8px 10px', textAlign: 'right', color: C.text, whiteSpace: 'nowrap' }}>{r.pvpAlvo != null ? `${fmt2(r.pvpAlvo)} €` : '—'}</td>
                        <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: '700', color: r.subida > 0 ? C.danger : C.textMuted, whiteSpace: 'nowrap' }}>
                          {r.subida != null && r.subida > 0 ? `+${fmt2(r.subida)} €` : '—'}
                        </td>
                      </tr>
                      </React.Fragment>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr style={{ background: C.bg, borderTop: `2px solid ${C.border}` }}>
                      <td colSpan={6} style={{ padding: '10px', fontWeight: '800', color: C.text }}>
                        {linhasMargens.length} produto(s) · margem média{' '}
                        <span style={{ color: margemColor(linhasMargens.length ? linhasMargens.reduce((s, r) => s + r.margem, 0) / linhasMargens.length : 0) }}>
                          {fmt2(linhasMargens.length ? linhasMargens.reduce((s, r) => s + r.margem, 0) / linhasMargens.length : 0)}%
                        </span>
                      </td>
                      <td style={{ padding: '10px', textAlign: 'right', fontSize: '10px', color: C.textMuted, textTransform: 'uppercase', fontWeight: '700' }}>Subida total</td>
                      <td style={{ padding: '10px', textAlign: 'right', fontWeight: '800', color: C.danger, whiteSpace: 'nowrap' }}>
                        +{fmt2(linhasMargens.reduce((s, r) => s + Math.max(0, r.subida || 0), 0))} €
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>

          {/* Sem custo — ponto cego */}
          {margens.semCusto.length > 0 && (
            <div style={{ background: C.surface, border: `1px solid ${C.warning}`, borderRadius: '6px', overflow: 'hidden', marginBottom: '16px' }}>
              <div style={{ padding: '10px 14px', borderBottom: `1px solid ${C.border}`, background: C.warningSoft, fontSize: '11px', fontWeight: '800', color: C.warning, textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                {margens.semCusto.length} produto(s) sem custo ou sem preço — não dá para saber a margem
              </div>
              <div style={{ padding: '10px 14px', display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                {margens.semCusto.map(s => (
                  <span key={s.id} title={`${s.semPreco ? 'Sem preço de venda' : 'Sem preco_custo em produtos_internos'}${s.associado ? ` · associado a ${s.associado}` : ''}`}
                    style={{
                      padding: '3px 8px', borderRadius: '3px', fontSize: '11px', color: C.text,
                      background: s.doCenario ? C.successSoft : C.bg,
                      border: `1px solid ${s.doCenario ? C.success : C.border}`,
                      fontWeight: s.doCenario ? '700' : '400',
                    }}>
                    {s.doCenario && '◆ '}{s.nome}{s.semPreco ? ' · sem preço' : ' · sem custo'}
                  </span>
                ))}
              </div>
            </div>
          )}

          <p style={{ fontSize: '11px', color: C.textMuted, margin: '0 0 20px', lineHeight: '1.6' }}>
            <strong>Como é calculado:</strong> receita = (preço − {fmt2(PORTES_COBRADOS)}) × (1 − desconto) + {fmt2(PORTES_COBRADOS)} — os {fmt2(PORTES_COBRADOS)} €/un cobrados ao cliente não sofrem desconto.
            Custo = <code>produtos_internos.preco_custo</code> + {fmt2(PORTES_CUSTO)} €/un que pagamos ao transportador — a diferença de {fmt2(PORTES_COBRADOS - PORTES_CUSTO)} €/un é margem nossa. Margem = lucro ÷ receita, a mesma definição da aba
            «Lucro realizado». O valor de <code>preco_custo</code> deve já vir com o desconto do fornecedor aplicado — se não vier, as margens aqui aparecem mais baixas do que a realidade.
          </p>
        </>
      )}

      {aba === 'encomendas' && (<>

      {/* FILTROS */}
      <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: '6px', padding: '12px 14px', marginBottom: '14px', display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center' }}>
        <label style={{ fontSize: '11px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px' }}><Calendar size={11} style={{ marginRight: '4px', verticalAlign: 'middle' }} />Período:</label>
        <select value={periodo} onChange={e => setPeriodo(e.target.value)}
          style={{ height: '30px', padding: '0 8px', border: `1px solid ${C.border}`, borderRadius: '3px', fontSize: '12px', color: C.text, background: C.surface, cursor: 'pointer' }}>
          {PERIODOS.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
        </select>
        {periodo === 'custom' && (
          <>
            <input type="date" value={customIni} onChange={e => setCustomIni(e.target.value)}
              style={{ height: '30px', padding: '0 8px', border: `1px solid ${C.border}`, borderRadius: '3px', fontSize: '12px' }} />
            <span style={{ fontSize: '12px', color: C.textMuted }}>até</span>
            <input type="date" value={customFim} onChange={e => setCustomFim(e.target.value)}
              style={{ height: '30px', padding: '0 8px', border: `1px solid ${C.border}`, borderRadius: '3px', fontSize: '12px' }} />
          </>
        )}
        <div style={{ height: '24px', width: '1px', background: C.border, margin: '0 4px' }} />
        <input type="text" placeholder="Filtrar cliente…" value={filtroCliente} onChange={e => setFiltroCliente(e.target.value)}
          style={{ height: '30px', padding: '0 8px', border: `1px solid ${C.border}`, borderRadius: '3px', fontSize: '12px', minWidth: '140px' }} />
        <input type="text" placeholder="Filtrar produto…" value={filtroProduto} onChange={e => setFiltroProduto(e.target.value)}
          style={{ height: '30px', padding: '0 8px', border: `1px solid ${C.border}`, borderRadius: '3px', fontSize: '12px', minWidth: '140px' }} />
        <input type="number" placeholder="Margem mín %" value={margemMinima} onChange={e => setMargemMinima(e.target.value)}
          style={{ height: '30px', padding: '0 8px', border: `1px solid ${C.border}`, borderRadius: '3px', fontSize: '12px', width: '110px' }} />
        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: C.textMuted, cursor: 'pointer', marginLeft: 'auto' }}>
          <input type="checkbox" checked={incluirInternos} onChange={e => setIncluirInternos(e.target.checked)} style={{ cursor: 'pointer', accentColor: C.purple }} />
          Incluir internos (sem lucro)
        </label>
      </div>

      {/* KPIs */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '10px', marginBottom: '16px' }}>
        {[
          { titulo: 'Lucro', valor: `${fmt2(kpis.lucro)} €`, sub: variacaoLucro != null ? `${variacaoLucro >= 0 ? '▲' : '▼'} ${Math.abs(variacaoLucro).toFixed(1)}% vs anterior` : 'sem comparação', cor: kpis.lucro >= 0 ? C.success : C.danger, icon: TrendingUp, iconColor: C.purple },
          { titulo: 'Margem', valor: `${fmt2(kpis.margem)} %`, sub: 'sobre receita s/IVA', cor: margemColor(kpis.margem), icon: Percent, iconColor: C.purple },
          { titulo: 'Faturado', valor: `${fmt2(kpis.faturado)} €`, sub: variacaoFat != null ? `${variacaoFat >= 0 ? '▲' : '▼'} ${Math.abs(variacaoFat).toFixed(1)}% vs anterior` : 'sem comparação', cor: C.text, icon: Wallet, iconColor: C.primary },
          { titulo: 'Custo fornecedor', valor: `${fmt2(kpis.custo)} €`, sub: 's/IVA · com desc. forn.', cor: C.text, icon: ShoppingCart, iconColor: C.warning },
          { titulo: 'Encomendas pagas', valor: `${kpis.encsPagas}`, sub: `${kpis.qtyVendida} un · ${fmt2(kpis.lucroMedioEnc)} €/enc`, cor: C.text, icon: Package, iconColor: C.primary },
          { titulo: 'Melhor produto', valor: kpis.melhorProduto ? `${fmt2(kpis.melhorProduto.lucro)} €` : '—', sub: kpis.melhorProduto ? kpis.melhorProduto.nome : 'sem dados', cor: C.success, icon: Award, iconColor: C.success },
        ].map(k => (
          <div key={k.titulo} style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: '6px', padding: '12px 14px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '10px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.3px', marginBottom: '6px' }}>
              <k.icon size={12} color={k.iconColor} /> {k.titulo}
            </div>
            <div style={{ fontSize: '20px', fontWeight: '800', color: k.cor, lineHeight: '1' }}>{k.valor}</div>
            <div style={{ fontSize: '10px', color: C.textMuted, marginTop: '4px', minHeight: '12px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={k.sub}>{k.sub}</div>
          </div>
        ))}
      </div>

      {/* INSIGHTS */}
      {insights.length > 0 && (
        <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: '6px', padding: '10px 14px', marginBottom: '16px' }}>
          <p style={{ margin: '0 0 6px', fontSize: '10px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.3px' }}>Insights</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {insights.map((ins, i) => (
              <div key={i} style={{ fontSize: '12px', color: ins.cor, fontWeight: '600' }}>• {ins.texto}</div>
            ))}
          </div>
        </div>
      )}

      {/* GRÁFICOS */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))', gap: '14px', marginBottom: '16px' }}>
        {/* Evolução 12 meses */}
        <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: '6px', padding: '12px 14px' }}>
          <p style={{ margin: '0 0 6px', fontSize: '11px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.3px' }}>Evolução — últimos 12 meses</p>
          <div style={{ width: '100%', height: 240 }}>
            <ResponsiveContainer>
              <ComposedChart data={serie12m} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
                <XAxis dataKey="label" style={{ fontSize: '10px' }} />
                <YAxis yAxisId="left" style={{ fontSize: '10px' }} tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} />
                <YAxis yAxisId="right" orientation="right" style={{ fontSize: '10px' }} tickFormatter={(v) => `${v}%`} />
                <RTooltip formatter={(v, n) => n === 'Margem %' ? `${v.toFixed(1)}%` : `${fmt2(v)} €`} />
                <Legend wrapperStyle={{ fontSize: '11px' }} />
                <Bar yAxisId="left" dataKey="lucro" fill={C.success} name="Lucro" />
                <Bar yAxisId="left" dataKey="faturado" fill={C.primary} name="Faturado" opacity={0.4} />
                <Line yAxisId="right" dataKey="margem" stroke={C.purple} name="Margem %" strokeWidth={2} dot={{ r: 3 }} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>
        {/* Pie composição */}
        <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: '6px', padding: '12px 14px' }}>
          <p style={{ margin: '0 0 6px', fontSize: '11px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.3px' }}>Composição do faturado — {labelPeriodo(periodo, periodIni, periodFim)}</p>
          {kpis.faturado > 0 ? (
            <div style={{ width: '100%', height: 240 }}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={composicao} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label={(d) => `${d.name}: ${fmt2(d.value)}€`}>
                    {composicao.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
                  </Pie>
                  <RTooltip formatter={(v) => `${fmt2(v)} €`} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div style={{ padding: '40px 0', textAlign: 'center', color: C.textMuted, fontSize: '12px' }}>Sem dados no período seleccionado</div>
          )}
        </div>
      </div>

      {/* Top produtos + Top clientes */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))', gap: '14px', marginBottom: '16px' }}>
        <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: '6px', padding: '12px 14px' }}>
          <p style={{ margin: '0 0 10px', fontSize: '11px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.3px' }}><Award size={11} style={{ marginRight: '4px', verticalAlign: 'middle' }} />Top 10 produtos por lucro</p>
          {topProdutos.length === 0 ? (
            <p style={{ fontSize: '12px', color: C.textMuted, fontStyle: 'italic', margin: 0 }}>Sem dados no período.</p>
          ) : (
            <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
              <tbody>
                {topProdutos.map((p, i) => (
                  <tr key={p.nome} style={{ borderBottom: `1px solid ${C.border}` }}>
                    <td style={{ padding: '6px 4px', width: '24px', color: C.textMuted, fontSize: '10px' }}>{i + 1}.</td>
                    <td style={{ padding: '6px 4px', color: C.text, maxWidth: '200px' }} title={p.nome}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.nome}</span>
                        {p.nome && <CopyButton text={p.nome} size={10} title="Copiar nome do artigo" />}
                      </span>
                    </td>
                    <td style={{ padding: '6px 4px', textAlign: 'right', color: C.textMuted, fontSize: '11px', whiteSpace: 'nowrap' }}>{p.qty}×</td>
                    <td style={{ padding: '6px 4px', textAlign: 'right', color: margemColor(p.margem), fontWeight: '700', fontSize: '11px', whiteSpace: 'nowrap' }}>{fmt2(p.margem)}%</td>
                    <td style={{ padding: '6px 4px', textAlign: 'right', color: C.success, fontWeight: '700', whiteSpace: 'nowrap' }}>{fmt2(p.lucro)} €</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: '6px', padding: '12px 14px' }}>
          <p style={{ margin: '0 0 10px', fontSize: '11px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.3px' }}><Users size={11} style={{ marginRight: '4px', verticalAlign: 'middle' }} />Top 10 clientes por lucro</p>
          {topClientes.length === 0 ? (
            <p style={{ fontSize: '12px', color: C.textMuted, fontStyle: 'italic', margin: 0 }}>Sem dados no período.</p>
          ) : (
            <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
              <tbody>
                {topClientes.map((c, i) => (
                  <tr key={c.cliente} style={{ borderBottom: `1px solid ${C.border}` }}>
                    <td style={{ padding: '6px 4px', width: '24px', color: C.textMuted, fontSize: '10px' }}>{i + 1}.</td>
                    <td style={{ padding: '6px 4px', color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '200px' }} title={c.cliente}>{c.cliente}</td>
                    <td style={{ padding: '6px 4px', textAlign: 'right', color: C.textMuted, fontSize: '11px', whiteSpace: 'nowrap' }}>{fmt2(c.faturado)}€</td>
                    <td style={{ padding: '6px 4px', textAlign: 'right', color: margemColor(c.margem), fontWeight: '700', fontSize: '11px', whiteSpace: 'nowrap' }}>{fmt2(c.margem)}%</td>
                    <td style={{ padding: '6px 4px', textAlign: 'right', color: C.success, fontWeight: '700', whiteSpace: 'nowrap' }}>{fmt2(c.lucro)} €</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* TABELA DETALHADA */}
      <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: '6px', overflow: 'hidden', marginBottom: '16px' }}>
        <div style={{ padding: '10px 14px', background: C.bg, borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
          <p style={{ margin: 0, fontSize: '11px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.3px' }}>
            Detalhe por encomenda · {linhas.length} resultado(s) · período: {labelPeriodo(periodo, periodIni, periodFim)}
          </p>
          <button onClick={todosExpandidos ? colapsarTudo : expandirTudo}
            style={{ height: '28px', padding: '0 10px', border: `1px solid ${C.border}`, borderRadius: '3px', background: C.surface, cursor: 'pointer', color: C.text, fontSize: '11px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '5px' }}>
            {todosExpandidos ? <><Minimize2 size={11} /> Colapsar</> : <><Maximize2 size={11} /> Expandir tudo</>}
          </button>
        </div>
        <div style={{ overflowX: 'auto' }}>
          {linhas.length === 0 ? (
            <div style={{ padding: '40px 18px', textAlign: 'center', color: C.textMuted, fontSize: '13px' }}>
              Sem encomendas para os filtros seleccionados.
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
              <thead>
                <tr style={{ background: '#fafafa' }}>
                  <th style={{ padding: '8px 6px', width: '22px', borderBottom: `1px solid ${C.border}` }}></th>
                  {['Encomenda / Produto','Cliente','Data','Qty','Receita s/IVA','Total c/IVA','Custo s/IVA','Lucro','Margem %','1ª','2ª','Realizado'].map(h => (
                    <th key={h} style={{ padding: '8px 8px', textAlign: ['Qty','Receita s/IVA','Total c/IVA','Custo s/IVA','Lucro','Margem %','Realizado'].includes(h) ? 'right' : (['1ª','2ª'].includes(h) ? 'center' : 'left'), fontSize: '10px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.3px', borderBottom: `1px solid ${C.border}`, whiteSpace: 'nowrap' }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {linhas.map(l => {
                  const expanded = expandedIds.has(l.id)
                  return (
                    <React.Fragment key={l.id}>
                      <tr onClick={() => toggleExpand(l.id)} style={{ borderBottom: `1px solid ${C.border}`, backgroundColor: l.realizado > 0 ? '#fafffa' : C.surface, cursor: 'pointer' }}>
                        <td style={{ padding: '8px 6px', verticalAlign: 'middle' }}>
                          {expanded ? <ChevronDown size={13} color={C.textMuted} /> : <ChevronRight size={13} color={C.textMuted} />}
                        </td>
                        <td style={{ padding: '8px 8px', fontWeight: '700', color: C.primary, whiteSpace: 'nowrap' }}>
                          {l.numero} <span style={{ color: C.textMuted, fontWeight: '500', fontSize: '11px' }}>· {l.items.length} produto(s)</span>
                        </td>
                        <td style={{ padding: '8px 8px', color: C.text, maxWidth: '160px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={l.cliente}>
                          {l.cliente}
                          {l.interno && <span title="Cliente interno" style={{ marginLeft: '5px', fontSize: '9px', fontWeight: '700', color: C.purple, background: '#f3f0ff', border: '1px solid #d1cef0', borderRadius: '8px', padding: '1px 5px', textTransform: 'uppercase' }}>int</span>}
                        </td>
                        <td style={{ padding: '8px 8px', color: C.textMuted, whiteSpace: 'nowrap' }}>{fmtDate(l.data)}</td>
                        <td style={{ padding: '8px 8px', textAlign: 'right', color: C.text, whiteSpace: 'nowrap' }}>{l.qtTotal}</td>
                        <td style={{ padding: '8px 8px', textAlign: 'right', color: C.text, fontWeight: '700', whiteSpace: 'nowrap' }}>{fmt2(l.receita)} €</td>
                        <td style={{ padding: '8px 8px', textAlign: 'right', color: C.success, fontWeight: '700', whiteSpace: 'nowrap' }}>{fmt2(l.receita * 1.23)} €</td>
                        <td style={{ padding: '8px 8px', textAlign: 'right', color: C.textMuted, fontWeight: '700', whiteSpace: 'nowrap' }}>{fmt2(l.custo)} €</td>
                        <td style={{ padding: '8px 8px', textAlign: 'right', fontWeight: '700', color: l.lucro >= 0 ? C.success : C.danger, whiteSpace: 'nowrap' }}>{fmt2(l.lucro)} €</td>
                        <td style={{ padding: '8px 8px', textAlign: 'right', fontWeight: '700', color: margemColor(l.margemPct), whiteSpace: 'nowrap' }}>{fmt2(l.margemPct)}%</td>
                        <td style={{ padding: '8px 8px', textAlign: 'center', whiteSpace: 'nowrap' }}>
                          {l.pago1Periodo ? <CheckCircle size={13} color={C.success} /> : l.pago1 ? <Clock size={13} color={C.textMuted} /> : <span style={{ color: C.textMuted }}>—</span>}
                        </td>
                        <td style={{ padding: '8px 8px', textAlign: 'center', whiteSpace: 'nowrap' }}>
                          {l.pago2Periodo ? <CheckCircle size={13} color={C.success} /> : l.pago2 ? <Clock size={13} color={C.textMuted} /> : <span style={{ color: C.textMuted }}>—</span>}
                        </td>
                        <td style={{ padding: '8px 8px', textAlign: 'right', fontWeight: '700', color: l.realizado > 0 ? C.success : C.textMuted, whiteSpace: 'nowrap' }}>{l.realizado > 0 ? `${fmt2(l.realizado)} €` : '—'}</td>
                      </tr>
                      {expanded && l.items.map(it => (
                        <tr key={it.id} style={{ borderBottom: `1px solid ${C.border}`, backgroundColor: it.semCusto ? '#fff8e1' : '#fafafa', fontSize: '11px' }}>
                          <td></td>
                          <td colSpan={2} style={{ padding: '6px 8px 6px 20px', color: C.text }}>
                            <div style={{ fontWeight: '600' }}>{it.nome}</div>
                            {it.ean && <div style={{ fontSize: '10px', color: C.textMuted, fontFamily: 'monospace' }}>EAN {it.ean}</div>}
                            <div style={{ fontSize: '10px', color: C.textMuted, marginTop: '3px' }}>
                              Venda/un: <strong style={{ color: C.text }}>{fmt2(it.precoUnitLiq)} €</strong> s/IVA
                              <span style={{ color: C.success, marginLeft: '4px' }}>· {fmt2(it.precoUnitLiq * 1.23)} € c/IVA</span>
                              {it.custoUnit != null && (
                                <>
                                  {' · '}Custo/un: <strong style={{ color: C.text }}>{fmt2(it.custoUnit)} €</strong> s/IVA
                                  <span style={{ marginLeft: '4px' }}>· {fmt2(it.custoUnit * 1.23)} € c/IVA</span>
                                </>
                              )}
                            </div>
                          </td>
                          <td></td>
                          <td style={{ padding: '6px 8px', textAlign: 'right', color: C.text, whiteSpace: 'nowrap' }}>{it.qty}</td>
                          <td style={{ padding: '6px 8px', textAlign: 'right', color: C.text, whiteSpace: 'nowrap' }}>{fmt2(it.receitaItem)} €</td>
                          <td style={{ padding: '6px 8px', textAlign: 'right', color: C.success, fontWeight: '700', whiteSpace: 'nowrap' }}>{fmt2(it.receitaItem * 1.23)} €</td>
                          <td style={{ padding: '6px 8px', textAlign: 'right', color: C.textMuted, whiteSpace: 'nowrap' }}>
                            {it.custoItem != null ? `${fmt2(it.custoItem)} €` : <span style={{ color: C.warning, fontStyle: 'italic' }}>—</span>}
                          </td>
                          <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: '700', color: it.lucroItem != null ? (it.lucroItem >= 0 ? C.success : C.danger) : C.warning, whiteSpace: 'nowrap' }}>
                            {it.lucroItem != null ? `${fmt2(it.lucroItem)} €` : 'sem custo'}
                          </td>
                          <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: '700', color: it.margemItem != null ? margemColor(it.margemItem) : C.warning, whiteSpace: 'nowrap' }}>
                            {it.margemItem != null ? `${fmt2(it.margemItem)}%` : '—'}
                          </td>
                          <td colSpan={3} style={{ padding: '6px 8px', textAlign: 'right', color: C.textMuted, fontSize: '10px', whiteSpace: 'nowrap' }}>
                            {it.lucroPorUn != null ? `lucro/un ${fmt2(it.lucroPorUn)} €` : ''}
                          </td>
                        </tr>
                      ))}
                    </React.Fragment>
                  )
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      </>)}

    </div>
  )
}
